import { useState } from 'react'
import { ChevronDownIcon, Filter } from 'lucide-react'

import { buttonVariants } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'

interface CategoryFilterProps {
  categories: string[]
  selected: string | null
  onSelect: (category: string | null) => void
}

const RadioDot = ({ checked }: { checked: boolean }) => (
  <span className={cn(
    'flex size-4 shrink-0 items-center justify-center rounded-full border',
    checked ? 'border-primary bg-primary' : 'border-muted-foreground/40'
  )}>
    {checked && <span className="size-2 rounded-full bg-primary-foreground" />}
  </span>
)

const CategoryFilter = ({ categories, selected, onSelect }: CategoryFilterProps) => {
  const [open, setOpen] = useState(false)
  const hasActiveFilter = selected !== null

  const handleSelect = (category: string | null) => {
    onSelect(category)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Filtrar por categoria"
          className={cn(
            buttonVariants({ variant: 'outline', size: 'default' }),
            'h-10 justify-between rounded-lg border-border bg-background px-3 py-2 text-sm font-normal text-foreground shadow-none hover:bg-background hover:text-foreground',
            'focus-visible:border-border focus-visible:ring-ring/70',
            open && 'border-border ring-2 ring-ring/60',
          )}
        >
          <span className="flex items-center gap-2">
            <Filter className="size-4 text-muted-foreground" />
            <span className={cn(!hasActiveFilter && 'text-muted-foreground')}>
              {hasActiveFilter ? selected : 'Categoria'}
            </span>
          </span>
          <ChevronDownIcon className={cn('size-4 text-muted-foreground transition-transform', open && 'rotate-180')} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={4}
        className="w-80 rounded-lg border-border p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="border-b border-border p-3 pb-1.5 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Categoria</span>
            <span className="text-xs text-muted-foreground">
              {hasActiveFilter ? '1' : categories.length} de {categories.length}
            </span>
          </div>
        </div>

        <ScrollArea className="max-h-[300px]">
          <div className="p-2 space-y-1" role="radiogroup" aria-label="Categorias">
            <button type="button" role="radio" aria-checked={!hasActiveFilter} onClick={() => handleSelect(null)} className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent cursor-pointer">
              <RadioDot checked={!hasActiveFilter} />
              <span className="font-medium text-foreground">Todas</span>
              <span className="ml-auto text-xs text-muted-foreground">{categories.length}</span>
            </button>
            {categories.map((category) => (
              <button
                type="button"
                role="radio"
                aria-checked={selected === category}
                key={category}
                onClick={() => handleSelect(selected === category ? null : category)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent cursor-pointer"
              >
                <RadioDot checked={selected === category} />
                <span className="text-foreground">{category}</span>
              </button>
            ))}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  )
}

export default CategoryFilter
